import api from '../api'
import type { Project, TimeEntry, CreateProjectPayload, ProjectSummary, TeamLogTimeResult, ProjectDetailSnapshot } from '../../types/time_tracking'

const EMPLOYEES_EXPAND = 'employees.id,employees.first_name,employees.last_name,employees.profile_picture'

export async function getProjects(params?: {
  status?: string
  billing_type?: string
  search?: string
  page?: number
  expand?: string
}) {
  const res = await api.get('/time-tracking/projects/', {
    params: { ...params, expand: params?.expand ?? EMPLOYEES_EXPAND },
  })
  return res.data
}

export async function getProjectById(id: string | number) {
  const res = await api.get(`/time-tracking/projects/${id}/`, {
    params: { expand: EMPLOYEES_EXPAND },
  })
  return res.data as Project
}

export async function getProjectSummary(id: string | number): Promise<ProjectSummary> {
  const res = await api.get(`/time-tracking/projects/${id}/summary/`, {
    params: { expand: EMPLOYEES_EXPAND },
  })
  return res.data
}

export async function getProjectDetailSnapshot(
  id: string | number,
  params?: { date_from?: string; date_to?: string },
): Promise<ProjectDetailSnapshot> {
  const res = await api.get(`/time-tracking/projects/${id}/detail_snapshot/`, {
    params: {
      ...params,
      expand: EMPLOYEES_EXPAND,
    },
  })
  return res.data
}
export const fetchActivityById = async (id: string) => {
  const response = await api.get(`/time-tracking/activities/${id}/`)
  return response.data
}

export async function createProject(payload: CreateProjectPayload) {
  const res = await api.post('/time-tracking/projects/', payload)
  return res.data as Project
}

export async function updateProject(id: string | number, payload: Partial<CreateProjectPayload>) {
  const res = await api.patch(`/time-tracking/projects/${id}/`, payload)
  return res.data as Project
}

export async function addProjectEmployees(id: string | number, employeeIds: string[]) {
  const res = await api.patch(`/time-tracking/projects/${id}/add_employees/`, { employees: employeeIds })
  return res.data as Project
}

export async function removeProjectEmployees(id: string | number, employeeIds: string[]) {
  const res = await api.patch(`/time-tracking/projects/${id}/remove_employees/`, { employees: employeeIds })
  return res.data as Project
}

export async function addProjectTeams(id: string | number, teamIds: string[]) {
  const res = await api.patch(`/time-tracking/projects/${id}/add_teams/`, { teams: teamIds })
  return res.data as Project
}

export async function removeProjectTeams(id: string | number, teamIds: string[]) {
  const res = await api.patch(`/time-tracking/projects/${id}/remove_teams/`, { teams: teamIds })
  return res.data as Project
}

export async function deleteProject(id: string | number) {
  await api.delete(`/time-tracking/projects/${id}/`)
}

export async function getTimeEntries(params?: {
  project?: string | number
  employee?: string | number
  user?: string | number
  date?: string
  date_from?: string
  date_to?: string
  page?: number
}) {
  const res = await api.get('/time-tracking/time-entries/', { 
    params: {
      ...params,
      expand: 'employee.id,employee.first_name,employee.last_name,employee.profile_picture'
    }
  })
  return res.data
}

export async function logTimeEntry(payload: {
  project: string | number
  employee: string | number
  date: string
  start_time?: string
  end_time?: string
  hours: number
  is_billable?: boolean
  note?: string
}) {
  const res = await api.post('/time-tracking/time-entries/', payload)
  return res.data as TimeEntry
}

export async function logTimeForTeam(payload: {
  project: string | number
  team: string | number
  date: string
  start_time?: string
  end_time?: string
  hours: number
  is_billable?: boolean
  note?: string
}) {
  const res = await api.post('/time-tracking/time-entries/log_for_team/', payload)
  return res.data as TeamLogTimeResult
}

export async function updateTimeEntry(
  id: string | number,
  payload: { start_time?: string; end_time?: string; hours?: number; note?: string; is_billable?: boolean }
) {
  const res = await api.patch(`/time-tracking/time-entries/${id}/`, payload)
  return res.data as TimeEntry
}

export async function deleteTimeEntry(id: string | number) {
  await api.delete(`/time-tracking/time-entries/${id}/`)
}

export async function getMyProjects(employeeUuid: string) {
  const res = await api.get('/time-tracking/projects/', {
    params: { employee: employeeUuid, expand: EMPLOYEES_EXPAND },
  })
  return res.data
}



// ---------------- PROJECTS ----------------

export const fetchProjectList = async ({
  search,
  page,
  pageSize,
  filters,
}: {
  search: string
  page: number
  pageSize: number
  filters: { [key: string]: string[] }
}) => {
  const params: Record<string, any> = {
    search,
    page,
    page_size: pageSize,
    expand: 'memberships,activities',
    fields:
      'id,name,client_name,billing_type,status,total_hours,billable_hours,billable_amount,activities.id,activities.name,memberships.id,memberships.employee_name,memberships.employee_profile_picture',
  }

  Object.entries(filters).forEach(([key, values]) => {
    if (values.length > 1) {
      params[`${key}__in`] = values.join(',')
    } else if (values.length === 1) {
      params[key] = values[0]
    }
  })

  const response = await api.get('/time-tracking/projects/', { params })
  return response.data
}

export const fetchProject = async (projectId: string) => {
  const response = await api.get(`/time-tracking/projects/${projectId}/`, {
    params: {
      expand: 'memberships,activities,teams',
      fields:
        'id,name,client_name,billing_type,status,total_hours,billable_hours,billable_amount,activities.id,activities.name,activities.description,memberships.id,memberships.employee.id,memberships.employee_name,memberships.employee_profile_picture,teams.id,teams.name',
    },
  })
  return response.data
}

export const fetchActivityList = async ({
  search,
  page,
  pageSize,
}: {
  search: string
  page: number
  pageSize: number
}) => {
  const response = await api.get('/time-tracking/activities/', {
    params: {
      search,
      page,
      page_size: pageSize,
    },
  })
  return response.data
}

export const createActivity = async (payload: {
  name: string
  description: string
}) => {
  const response = await api.post('/time-tracking/activities/', payload)
  return response.data
}

export const updateActivity = async (payload: {
  id: string
  name?: string
  description?: string
}) => {
  const response = await api.patch(`/time-tracking/activities/${payload.id}/`, payload)
  return response.data
}

export const deleteActivity = async (activityId: string) => {
  const response = await api.delete(`/time-tracking/activities/${activityId}/`)
  return response.data
}
